import { z } from 'zod';
import type { ErrorCode } from './common';

/**
 * Request-side schemas: limits, normalisation and rejections in one place. They
 * live here so the browser applies the identical rule before a request goes out
 * rather than re-deriving it. Every rejection's `message` is an `ErrorCode`,
 * which the API returns as-is and the web translates.
 */

export const NAME_MAX = 100;
export const LABEL_MAX = 100;
export const URL_MAX = 2048;
export const NOTE_MAX = 2_000;
export const QUESTION_MAX = 500;
export const GRAMS_MAX = 5_000;
/** Pure fat is ~900 kcal/100g; nothing edible is denser. */
export const KCAL_PER_100G_MAX = 900;
/** How many of one product a single entry can count. */
export const QUANTITY_MAX = 999;
/** How many ingredients one dish can list. */
export const INGREDIENTS_MAX = 50;
export const DAILY_LIMIT_MIN = 500;
export const DAILY_LIMIT_MAX = 9_999;
/** What a user who never set a limit gets. */
export const CALORIE_DAILY_LIMIT_DEFAULT = 2_000;
/** How the day view sums the day up: two number cards, or a donut chart. */
export const CALORIE_DAY_VIEWS = ['cards', 'chart'] as const;
export type CalorieDayView = (typeof CALORIE_DAY_VIEWS)[number];
/** What a user who never picked a view gets. */
export const CALORIE_DAY_VIEW_DEFAULT: CalorieDayView = 'chart';
/** Body weight in kg — wide enough for anyone, narrow enough to catch "8,1". */
export const WEIGHT_MIN = 20;
export const WEIGHT_MAX = 400;

/** Trimmed, 1..100 — wallet and goal names. */
export const nameInput = z
  .string()
  .transform((raw) => raw.trim())
  .refine((s) => s.length >= 1 && s.length <= NAME_MAX, {
    message: 'name_required' satisfies ErrorCode,
  });

/**
 * Accepts what a Polish keyboard produces — "1 234,56 zł", thin spaces, comma
 * decimals — and normalises to a plain 2dp string, rounding half up. Rejects
 * negatives and non-numbers.
 */
export const amountInput = z.string().transform((raw, ctx) => {
  const trimmed = raw.trim();
  const normalized = trimmed
    .replace(/[\s\u00A0\u202F]/g, '')
    .replace(/z[łl]$/i, '')
    .replace(',', '.');
  const n = trimmed === '' ? Number.NaN : Number(normalized);
  if (!Number.isFinite(n) || n < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'amount_invalid' satisfies ErrorCode,
    });
    return z.NEVER;
  }
  return (Math.round(n * 100) / 100).toFixed(2);
});

/** Same as `amountInput` but rejects zero — goal targets must be positive. */
export const positiveAmountInput = amountInput.refine((s) => Number(s) > 0, {
  message: 'target_amount_invalid' satisfies ErrorCode,
});

/**
 * `<input type="month">` gives "YYYY-MM"; goals are stored as the first of that
 * month.
 */
export const monthInput = z.string().transform((raw, ctx) => {
  const m = raw.trim().match(/^(\d{4})-(0[1-9]|1[0-2])$/);
  if (!m) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'month_required' satisfies ErrorCode,
    });
    return z.NEVER;
  }
  return `${m[1]}-${m[2]}-01`;
});

export const labelInput = z
  .string()
  .transform((raw) => raw.trim())
  .refine((s) => s.length >= 1 && s.length <= LABEL_MAX, {
    message: 'label_required' satisfies ErrorCode,
  });

/** http(s) only, normalised through the URL parser. */
export const urlInput = z.string().transform((raw, ctx) => {
  const s = raw.trim();
  const invalid = () => {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'url_invalid' satisfies ErrorCode,
    });
    return z.NEVER;
  };
  if (s.length < 1 || s.length > URL_MAX) return invalid();
  try {
    const u = new URL(s);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return invalid();
    return u.toString();
  } catch {
    return invalid();
  }
});

/**
 * One remembered fact. Deliberately short: a note is a
 * fact, not a document, and every retrieved note is paid for twice — once to
 * embed it, once as context in the answer.
 */
export const noteInput = z
  .string()
  .transform((raw) => raw.trim())
  .refine((s) => s.length >= 1 && s.length <= NOTE_MAX, {
    message: 'note_required' satisfies ErrorCode,
  });

/** A plain-language question. */
export const questionInput = z
  .string()
  .transform((raw) => raw.trim())
  .refine((s) => s.length >= 1 && s.length <= QUESTION_MAX, {
    message: 'question_required' satisfies ErrorCode,
  });

/** Trimmed, 1..100 — a product, whether logged on a day or kept in the catalog. */
export const productNameInput = z
  .string()
  .transform((raw) => raw.trim())
  .refine((s) => s.length >= 1 && s.length <= NAME_MAX, {
    message: 'product_name_required' satisfies ErrorCode,
  });

/** Trimmed, 1..100 — a section of the day, such as "Śniadanie". */
export const mealNameInput = z
  .string()
  .transform((raw) => raw.trim())
  .refine((s) => s.length >= 1 && s.length <= NAME_MAX, {
    message: 'meal_name_required' satisfies ErrorCode,
  });

/**
 * A form field holding a whole number in `min..max`. Arrives as the input's
 * string and leaves as a number; "150,5" and "1e3" are rejected rather than
 * guessed at.
 */
function wholeNumberInput(message: ErrorCode, min: number, max: number) {
  return z.string().transform((raw, ctx) => {
    const trimmed = raw.trim();
    const n = /^\d+$/.test(trimmed) ? Number(trimmed) : Number.NaN;
    if (!Number.isInteger(n) || n < min || n > max) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message });
      return z.NEVER;
    }
    return n;
  });
}

/**
 * `wholeNumberInput` that also takes a blank field, as `null` — a product may
 * be logged before its numbers are known and finished later.
 */
function optionalWholeNumberInput(
  message: ErrorCode,
  min: number,
  max: number,
) {
  const required = wholeNumberInput(message, min, max);
  return z.string().transform((raw, ctx) => {
    if (raw.trim() === '') return null;
    const parsed = required.safeParse(raw);
    if (!parsed.success) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message });
      return z.NEVER;
    }
    return parsed.data;
  });
}

export const optionalGramsInput = optionalWholeNumberInput(
  'grams_invalid',
  1,
  GRAMS_MAX,
);

export const optionalKcalPer100gInput = optionalWholeNumberInput(
  'kcal_invalid',
  0,
  KCAL_PER_100G_MAX,
);

/**
 * How many of the product — "2", "0,5", "1.25". Comma or dot, at most two
 * decimals (the column's scale), above zero.
 */
export const quantityInput = z.string().transform((raw, ctx) => {
  const trimmed = raw.trim().replace(',', '.');
  const n = /^\d+(\.\d{1,2})?$/.test(trimmed) ? Number(trimmed) : Number.NaN;
  if (!(n > 0 && n <= QUANTITY_MAX)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'quantity_invalid' satisfies ErrorCode,
    });
    return z.NEVER;
  }
  return n;
});

/**
 * Body weight in kg — "81,2", "81.2", "81". Comma or dot, at most one decimal
 * (the column's scale), within `WEIGHT_MIN`..`WEIGHT_MAX`.
 */
export const weightInput = z.string().transform((raw, ctx) => {
  const trimmed = raw.trim().replace(',', '.');
  const n = /^\d+(\.\d)?$/.test(trimmed) ? Number(trimmed) : Number.NaN;
  if (!(n >= WEIGHT_MIN && n <= WEIGHT_MAX)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'weight_invalid' satisfies ErrorCode,
    });
    return z.NEVER;
  }
  return n;
});

export const dailyLimitInput = wholeNumberInput(
  'daily_limit_invalid',
  DAILY_LIMIT_MIN,
  DAILY_LIMIT_MAX,
);

/** `<input type="date">` gives "YYYY-MM-DD"; anything that is not a real day is rejected. */
export const dayInput = z.string().transform((raw, ctx) => {
  const s = raw.trim();
  if (!isRealDay(s)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'day_invalid' satisfies ErrorCode,
    });
    return z.NEVER;
  }
  return s;
});

/** "2026-02-30" matches the pattern but is not a day — round-trip it through UTC to tell. */
export function isRealDay(s: string): boolean {
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.toISOString().slice(0, 10) === s;
}

export const RECIPE_SERVINGS_MAX = 99;
export const RECIPE_MINUTES_MAX = 9_999;
export const RECIPE_INGREDIENTS_MAX = 100;
export const RECIPE_INGREDIENT_MAX = 200;
export const RECIPE_STEPS_MAX = 50;
export const RECIPE_STEP_MAX = 1_000;
export const RECIPE_TAGS_MAX = 20;
export const RECIPE_TAG_MAX = 40;
export const RECIPE_NOTES_MAX = 5_000;

/**
 * An ordered list of plain-text lines: each trimmed, blanks dropped, then held
 * to `maxCount` lines of `maxLength`. The form sends its rows as typed and
 * leaves the cleanup to this. Past a limit there is no dedicated code — the
 * form's `maxLength` keeps a person from getting there.
 */
export function recipeLinesInput(maxCount: number, maxLength: number) {
  return z
    .array(z.string())
    .transform((lines) => lines.map((l) => l.trim()).filter((l) => l !== ''))
    .refine(
      (lines) =>
        lines.length <= maxCount && lines.every((l) => l.length <= maxLength),
      { message: 'invalid_request' satisfies ErrorCode },
    );
}

/**
 * Free tags, lower-cased so "Zupa" and "zupa" are one chip, deduplicated
 * keeping the first occurrence's place.
 */
export const recipeTagsInput = z
  .array(z.string())
  .transform((tags) => [
    ...new Set(
      tags.map((t) => t.trim().toLocaleLowerCase('pl')).filter((t) => t !== ''),
    ),
  ])
  .refine(
    (tags) =>
      tags.length <= RECIPE_TAGS_MAX &&
      tags.every((t) => t.length <= RECIPE_TAG_MAX),
    { message: 'invalid_request' satisfies ErrorCode },
  );

/** Trimmed; blank is no notes at all. */
export const recipeNotesInput = z
  .string()
  .transform((raw) => raw.trim() || null)
  .refine((s) => s === null || s.length <= RECIPE_NOTES_MAX, {
    message: 'invalid_request' satisfies ErrorCode,
  });

/** Servings, informational only: a blank field is `null`. */
export const optionalServingsInput = optionalWholeNumberInput(
  'invalid_request',
  1,
  RECIPE_SERVINGS_MAX,
);

/** Total time in minutes; a blank field is `null`. */
export const optionalMinutesInput = optionalWholeNumberInput(
  'invalid_request',
  1,
  RECIPE_MINUTES_MAX,
);

export const CHECKLIST_ITEMS_MAX = 200;
export const CHECKLIST_ITEM_MAX = 200;
export const CHECKLIST_CHILDREN_MAX = 20;

/**
 * A standard list's rows as the form sends them, in order. A row with an `id`
 * is an existing item kept through the edit, tick and all; a row without one is
 * new. Texts are trimmed and blank rows dropped — an id on a dropped row means
 * that item goes. Past a limit there is no dedicated code, as for recipes.
 */
export const checklistItemsInput = z
  .array(z.object({ id: z.string().uuid().optional(), text: z.string() }))
  .transform((items) =>
    items
      .map((i) => ({ id: i.id, text: i.text.trim() }))
      .filter((i) => i.text !== ''),
  )
  .refine(
    (items) =>
      items.length <= CHECKLIST_ITEMS_MAX &&
      items.every((i) => i.text.length <= CHECKLIST_ITEM_MAX),
    { message: 'invalid_request' satisfies ErrorCode },
  );

/**
 * The standard lists a combined list is made of, in the order they show in,
 * deduplicated keeping the first occurrence; at least one.
 */
export const checklistChildIdsInput = z
  .array(z.string().uuid())
  .transform((ids) => [...new Set(ids)])
  .refine((ids) => ids.length >= 1, {
    message: 'checklist_children_required' satisfies ErrorCode,
  })
  .refine((ids) => ids.length <= CHECKLIST_CHILDREN_MAX, {
    message: 'invalid_request' satisfies ErrorCode,
  });
